import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLeads } from '@/hooks/useLeads';
import { useDeals } from '@/hooks/useDeals';
import { useActionEvents, useDashboardStats } from '@/hooks/useActionEvents';
import { LoadingState } from '@/components/LoadingState';
import { EmptyState } from '@/components/EmptyState';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { ExecutionScore } from '@/components/insights/ExecutionScore';
import { KPICard } from '@/components/insights/KPICard';
import { StageVelocityChart } from '@/components/insights/StageVelocityChart';
import {
  TrendingUp,
  Activity,
  AlertTriangle,
  Target,
  DollarSign,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from 'recharts';
import { format, subDays, differenceInDays } from 'date-fns';
import { cn } from '@/lib/utils';

const STAGE_COLORS = {
  new: 'hsl(220, 14%, 70%)',
  contacted: 'hsl(199, 89%, 48%)',
  appointment: 'hsl(262, 83%, 58%)',
  proposal: 'hsl(38, 92%, 50%)',
  won: 'hsl(142, 71%, 45%)',
  lost: 'hsl(0, 72%, 51%)',
};

const SOURCE_COLORS = [
  'hsl(224, 76%, 48%)',
  'hsl(142, 71%, 45%)',
  'hsl(38, 92%, 50%)',
  'hsl(262, 83%, 58%)',
  'hsl(199, 89%, 48%)',
  'hsl(0, 72%, 51%)',
];

const REASON_COLORS = [
  'hsl(224, 76%, 48%)',
  'hsl(38, 92%, 50%)',
  'hsl(262, 83%, 58%)',
  'hsl(199, 89%, 48%)',
  'hsl(142, 71%, 45%)',
];

const STALE_THRESHOLD_DAYS = 7;

function formatCurrency(value: number): string {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

export default function Insights() {
  const navigate = useNavigate();
  const { data: leads, isLoading: leadsLoading } = useLeads();
  const { data: deals, isLoading: dealsLoading } = useDeals();
  const { data: stats, isLoading: statsLoading } = useDashboardStats();
  const { data: events } = useActionEvents(30);

  const isLoading = leadsLoading || dealsLoading || statsLoading;

  // Execution Momentum: actions per day (30d)
  const executionData = useMemo(() => {
    if (!stats) return [];
    const data = [];
    for (let i = 29; i >= 0; i--) {
      const date = format(subDays(new Date(), i), 'yyyy-MM-dd');
      const displayDate = format(subDays(new Date(), i), 'MMM d');
      data.push({
        date: displayDate,
        actions: stats.actionsDaily30[date] || 0,
      });
    }
    return data;
  }, [stats]);

  // Funnel Conversion by stage
  const funnelData = useMemo(() => {
    if (!leads) return [];
    const stageCounts: Record<string, number> = {
      new: 0,
      contacted: 0,
      appointment: 0,
      proposal: 0,
      won: 0,
    };
    leads.forEach(l => {
      const stage = l.pipeline_stage || 'new';
      if (stage in stageCounts) {
        stageCounts[stage]++;
      }
    });
    return [
      { name: 'New', value: stageCounts.new, fill: STAGE_COLORS.new },
      { name: 'Contacted', value: stageCounts.contacted, fill: STAGE_COLORS.contacted },
      { name: 'Appointment', value: stageCounts.appointment, fill: STAGE_COLORS.appointment },
      { name: 'Proposal', value: stageCounts.proposal, fill: STAGE_COLORS.proposal },
      { name: 'Won', value: stageCounts.won, fill: STAGE_COLORS.won },
    ];
  }, [leads]);

  // Stale Risk (leads without recent activity)
  const staleMetrics = useMemo(() => {
    if (!leads || !deals) return { count: 0, value: 0, leadIds: [] as string[] };
    const now = new Date();
    const staleLeads = leads.filter(l => {
      if (l.pipeline_stage === 'won' || l.pipeline_stage === 'lost') return false;
      if (!l.last_interaction_at) return true;
      return differenceInDays(now, new Date(l.last_interaction_at)) > STALE_THRESHOLD_DAYS;
    });
    const staleValue = staleLeads.reduce((sum, l) => {
      const deal = deals.find(d => d.lead_id === l.id && d.stage !== 'won' && d.stage !== 'lost');
      return sum + (deal?.value || 0);
    }, 0);
    return { 
      count: staleLeads.length, 
      value: staleValue,
      leadIds: staleLeads.map(l => l.id),
    };
  }, [leads, deals]);

  // Source Performance
  const sourcePerformance = useMemo(() => {
    if (!leads || !deals) return [];
    const sourceStats: Record<string, { leads: number; won: number; revenue: number }> = {};
    
    leads.forEach(l => {
      const source = l.source || 'other';
      if (!sourceStats[source]) {
        sourceStats[source] = { leads: 0, won: 0, revenue: 0 };
      }
      sourceStats[source].leads++;
    });

    deals.filter(d => d.stage === 'won').forEach(d => {
      const lead = leads.find(l => l.id === d.lead_id);
      const source = lead?.source || 'other';
      if (sourceStats[source]) {
        sourceStats[source].won++;
        sourceStats[source].revenue += d.value || 0;
      }
    });

    return Object.entries(sourceStats)
      .map(([source, stats], index) => ({
        source: source.charAt(0).toUpperCase() + source.slice(1),
        leads: stats.leads,
        won: stats.won,
        winRate: stats.leads > 0 ? Math.round((stats.won / stats.leads) * 100) : 0,
        revenue: stats.revenue,
        color: SOURCE_COLORS[index % SOURCE_COLORS.length],
      }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [leads, deals]);

  // Win/Loss reasons (decision-first: read from deals, not leads)
  const outcomeReasons = useMemo(() => {
    if (!deals) return { won: [], lost: [] };
    
    // Get lost deals (commercial outcomes)
    const lostDeals = deals.filter(d => {
      const dealStatus = (d as any).deal_status;
      return dealStatus === 'lost' || d.stage === 'lost';
    });
    
    const reasonCounts: Record<string, number> = {};
    lostDeals.forEach(d => {
      // Read loss_reason from deal (commercial outcome)
      const reason = (d as any).loss_reason || 'No reason';
      // Map enum values to readable labels
      const reasonLabel = reason === 'no_budget' ? 'No Budget' 
        : reason === 'not_interested' ? 'Not Interested'
        : reason === 'competitor' ? 'Competitor'
        : reason === 'timing' ? 'Timing / Not Now'
        : reason === 'ghosted' ? 'Ghosted / No Response'
        : reason === 'internal_decision' ? 'Internal Decision'
        : reason === 'other' ? 'Other'
        : reason === 'price' ? 'Price'
        : reason; // Fallback to original value
      reasonCounts[reasonLabel] = (reasonCounts[reasonLabel] || 0) + 1;
    });
    
    const lostReasons = Object.entries(reasonCounts)
      .map(([reason, count], index) => ({
        reason,
        count,
        color: REASON_COLORS[index % REASON_COLORS.length],
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return { won: [], lost: lostReasons };
  }, [deals]);

  // Correct Conversion Rate (decision-first: use deal_status, not stage)
  const conversionMetrics = useMemo(() => {
    if (!deals) return { winRate: 0, wonCount: 0, lostCount: 0 };
    // Use deal_status (commercial outcome) instead of stage (process)
    const wonDeals = deals.filter(d => (d as any).deal_status === 'won' || d.stage === 'won');
    const lostDeals = deals.filter(d => (d as any).deal_status === 'lost' || d.stage === 'lost');
    const closedTotal = wonDeals.length + lostDeals.length;
    return {
      winRate: closedTotal > 0 ? (wonDeals.length / closedTotal) * 100 : 0,
      wonCount: wonDeals.length,
      lostCount: lostDeals.length,
    };
  }, [deals]);

  // Execution Score metrics
  const executionScoreMetrics = useMemo(() => {
    if (!leads || !stats || !events) {
      return {
        actionsLast7Days: 0,
        contactedRate: 0,
        followupCompliance: 0,
        pipelineMovements: 0,
        staleLeadsCount: 0,
        totalActiveLeads: 0,
      };
    }

    const activeLeads = leads.filter(l => l.pipeline_stage !== 'won' && l.pipeline_stage !== 'lost');
    const totalActiveLeads = activeLeads.length;

    // Contacted rate: % of new leads that have been contacted
    const newLeads = leads.filter(l => l.pipeline_stage === 'new');
    const contactedOrBeyond = leads.filter(l => 
      l.pipeline_stage && ['contacted', 'appointment', 'proposal', 'won'].includes(l.pipeline_stage)
    );
    const contactedRate = leads.length > 0 
      ? (contactedOrBeyond.length / leads.length) * 100 
      : 0;

    // Followup compliance: % of leads with next_action_at that are not overdue
    const leadsWithNextAction = activeLeads.filter(l => l.next_action_at);
    const now = new Date();
    const overdueLeads = leadsWithNextAction.filter(l => new Date(l.next_action_at!) < now);
    const followupCompliance = leadsWithNextAction.length > 0
      ? ((leadsWithNextAction.length - overdueLeads.length) / leadsWithNextAction.length) * 100
      : 100;

    // Pipeline movements from events
    const pipelineMovements = events.filter(e => e.action_type === 'stage_change').length;

    return {
      actionsLast7Days: stats.actionsLast7Days || 0,
      contactedRate: Math.round(contactedRate),
      followupCompliance: Math.round(followupCompliance),
      pipelineMovements,
      staleLeadsCount: staleMetrics.count,
      totalActiveLeads,
    };
  }, [leads, stats, events, staleMetrics.count]);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <LoadingState message="Loading insights..." />
      </div>
    );
  }

  if (!leads || leads.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <EmptyState
          icon={TrendingUp}
          title="No data yet"
          description="Add leads and take actions to see performance insights."
          action={
            <Button variant="outline" asChild>
              <a href="/app/leads">Add Leads</a>
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6 max-w-7xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <TrendingUp className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-foreground">Insights</h1>
          <p className="text-sm text-muted-foreground">What's working and what's hurting your revenue</p>
        </div>
      </div>

      {/* Execution Score */}
      <ExecutionScore {...executionScoreMetrics} />

      {/* Key Metrics - Clickable */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard
          icon={<Target className="w-5 h-5" />}
          value={`${conversionMetrics.winRate.toFixed(0)}%`}
          label="Win Rate"
          iconBgClass="bg-success/10"
          iconClass="text-success"
          navigateTo="/app/pipeline?stage=won"
        />
        
        <KPICard
          icon={<Activity className="w-5 h-5" />}
          value={stats?.actionsLast7Days || 0}
          label="Actions (7d)"
          iconBgClass="bg-primary/10"
          iconClass="text-primary"
          navigateTo="/app/today"
        />

        <KPICard
          icon={<AlertTriangle className="w-5 h-5" />}
          value={staleMetrics.count}
          label="Stale Leads"
          iconBgClass={staleMetrics.count > 0 ? 'bg-warning/10' : 'bg-muted'}
          iconClass={staleMetrics.count > 0 ? 'text-warning' : 'text-muted-foreground'}
          navigateTo={`/app/leads?filter=stale&days=${STALE_THRESHOLD_DAYS}`}
        />

        <KPICard
          icon={<DollarSign className="w-5 h-5" />}
          value={formatCurrency(staleMetrics.value)}
          label="At Risk"
          iconBgClass="bg-destructive/10"
          iconClass="text-destructive"
          navigateTo="/app/pipeline?filter=at_risk"
        />
      </div>

      {/* Charts Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Execution Momentum */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Execution Momentum</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={executionData}>
                <defs>
                  <linearGradient id="colorMomentum" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 10 }} 
                  tickLine={false} 
                  axisLine={false}
                  interval={4}
                />
                <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={30} />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="actions" 
                  stroke="hsl(var(--primary))" 
                  strokeWidth={2}
                  fill="url(#colorMomentum)" 
                />
              </AreaChart>
            </ResponsiveContainer>
            {/* Automatic insight */}
            <div className="mt-3 pt-3 border-t border-border/50">
              <p className="text-xs text-muted-foreground mb-2">
                {(() => {
                  const avgActions = executionData.length > 0 
                    ? Math.round(executionData.reduce((sum, d) => sum + d.actions, 0) / executionData.length)
                    : 0;
                  if (avgActions < 5) {
                    return "Most deals are lost after 5 days without contact. You're below the minimum activity threshold.";
                  } else if (avgActions < 10) {
                    return "You're leaving revenue on the table. 10+ actions per day = 40% higher close rates.";
                  } else {
                    return "Strong execution. This pace drives revenue. Don't slow down.";
                  }
                })()}
              </p>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 text-xs gap-1"
                onClick={() => navigate('/app/today')}
              >
                Fix this today
                <ArrowRight className="w-3 h-3" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Funnel Conversion */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Pipeline Funnel</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={funnelData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  tick={{ fontSize: 11 }} 
                  tickLine={false} 
                  axisLine={false}
                  width={70}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {funnelData.map((entry, index) => (
                    <Cell key={index} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {/* Automatic insight */}
            <div className="mt-3 pt-3 border-t border-border/50">
              <p className="text-xs text-muted-foreground mb-2">
                {(() => {
                  const newCount = funnelData.find(d => d.name === 'New')?.value || 0;
                  const proposalCount = funnelData.find(d => d.name === 'Proposal')?.value || 0;
                  if (newCount > proposalCount * 3) {
                    return "You're hoarding leads. Move them to Proposal or you'll lose them.";
                  } else if (proposalCount === 0 && newCount > 0) {
                    return "Zero deals in Proposal. This is why revenue is stuck. Advance qualified leads now.";
                  } else {
                    return "Pipeline is moving. Keep pushing deals forward or they'll stall.";
                  }
                })()}
              </p>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 text-xs gap-1"
                onClick={() => navigate('/app/pipeline')}
              >
                Review affected deals
                <ArrowRight className="w-3 h-3" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stage Velocity - Now with proper empty state */}
        <StageVelocityChart 
          leads={leads || []} 
          events={events?.map(e => ({
            action_type: e.action_type,
            created_at: e.created_at,
            lead_id: e.lead_id,
            meta: e.meta as { from_stage?: string; to_stage?: string } | null,
          }))}
        />

        {/* Source Performance */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Source Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {sourcePerformance.slice(0, 5).map((source) => (
                <div key={source.source} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-2 h-2 rounded-full" 
                      style={{ backgroundColor: source.color }}
                    />
                    <span className="text-sm">{source.source}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-muted-foreground">{source.leads} leads</span>
                    <span className="text-muted-foreground">{source.winRate}% win</span>
                    <span className="font-medium">{formatCurrency(source.revenue)}</span>
                  </div>
                </div>
              ))}
            </div>
            {/* Automatic insight */}
            <div className="mt-3 pt-3 border-t border-border/50">
              <p className="text-xs text-muted-foreground mb-2">
                {(() => {
                  if (sourcePerformance.length === 0) return "No source data available yet.";
                  const topSource = sourcePerformance[0];
                  const avgWinRate = sourcePerformance.reduce((sum, s) => sum + s.winRate, 0) / sourcePerformance.length;
                  if (topSource.winRate > avgWinRate * 1.5) {
                    return `${topSource.source} is working (${topSource.winRate}% win rate). Stop wasting time on low-performing sources.`;
                  } else if (topSource.winRate < 20) {
                    return "All sources are underperforming. Your qualification or follow-up process is broken.";
                  } else {
                    return `${topSource.source} = ${formatCurrency(topSource.revenue)}. This is where you should focus.`;
                  }
                })()}
              </p>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 text-xs gap-1"
                onClick={() => navigate('/app/leads')}
              >
                Review leads by source
                <ArrowRight className="w-3 h-3" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Win/Loss Reasons */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Loss Reasons</CardTitle>
          </CardHeader>
          <CardContent>
            {outcomeReasons.lost.length > 0 ? (
              <>
                <div className="flex gap-4">
                  <div className="w-1/2">
                    <ResponsiveContainer width="100%" height={140}>
                      <PieChart>
                        <Pie
                          data={outcomeReasons.lost}
                          cx="50%"
                          cy="50%"
                          innerRadius={30}
                          outerRadius={55}
                          dataKey="count"
                          nameKey="reason"
                        >
                          {outcomeReasons.lost.map((entry, index) => (
                            <Cell key={index} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                            fontSize: '12px',
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="w-1/2 space-y-2">
                    {outcomeReasons.lost.map((reason) => (
                      <div key={reason.reason} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: reason.color }}
                          />
                          <span className="text-muted-foreground truncate max-w-[100px]">
                            {reason.reason}
                          </span>
                        </div>
                        <span className="font-medium">{reason.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Automatic insight */}
                <div className="mt-3 pt-3 border-t border-border/50">
                <p className="text-xs text-muted-foreground mb-2">
                  {(() => {
                    const topReason = outcomeReasons.lost[0];
                    if (topReason.reason.toLowerCase().includes('timing') || topReason.reason.toLowerCase().includes('not now')) {
                      return "Timing kills deals. Follow up every 3-5 days or you'll miss the window.";
                    } else if (topReason.reason.toLowerCase().includes('ghosted') || topReason.reason.toLowerCase().includes('no response')) {
                      return "No response = you waited too long. Most deals die after 5 days without contact.";
                    } else if (topReason.reason.toLowerCase().includes('price')) {
                      return "Price objections mean you qualified wrong or showed value too late. Fix this.";
                    } else {
                      return `${topReason.reason} killed ${topReason.count} deals. This pattern is costing you revenue.`;
                    }
                  })()}
                </p>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 text-xs gap-1"
                  onClick={() => navigate('/app/today')}
                >
                  Prevent this today
                  <ArrowRight className="w-3 h-3" />
                </Button>
              </div>
            </>
            ) : (
              <div className="h-[140px] flex items-center justify-center text-sm text-muted-foreground">
                No lost deals recorded yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
